import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, LogOut, Trash2, Users, Sparkles, RefreshCw, GraduationCap } from "lucide-react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS = {
  yeni: { label: "Yeni", cls: "bg-[#FFE500]/15 text-[#141178]" },
  iletisim_kuruldu: { label: "İletişim Kuruldu", cls: "bg-[#1B19A8]/12 text-[#1B19A8]" },
  kaydedildi: { label: "Kaydedildi", cls: "bg-green-100 text-green-700" },
  arsiv: { label: "Arşiv", cls: "bg-gray-200 text-gray-600" },
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        api.get("/applications"),
        api.get("/applications/stats"),
      ]);
      setApps(a.data);
      setStats(s.data);
    } catch {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/applications/${id}`, { status });
      setApps((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Durum güncellendi");
    } catch {
      toast.error("Güncellenemedi");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu başvuruyu silmek istediğine emin misin?")) return;
    try {
      await api.delete(`/applications/${id}`);
      setApps((prev) => prev.filter((x) => x.id !== id));
      toast.success("Başvuru silindi");
    } catch {
      toast.error("Silinemedi");
    }
  };

  const exportCsv = () => {
    window.open(`${API}/applications/export`, "_blank");
  };

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[#f7f9fa]">
      <header className="bg-white border-b border-black/5 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center w-9 h-9 rounded-xl bg-[#1B19A8] text-white"><GraduationCap size={20} /></span>
            <span className="font-display text-lg font-bold text-[#1a1a1a]">Study in KZ · Panel</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-[#1a1a1a]/60">{user?.email}</span>
            <button onClick={doLogout} data-testid="logout-btn" className="inline-flex items-center gap-2 rounded-full border border-black/10 px-4 py-2 text-sm font-medium text-[#1a1a1a] hover:bg-[#f7f9fa] transition-colors">
              <LogOut size={16} /> Çıkış
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold text-[#1a1a1a]">Başvurular</h1>
            <p className="text-[#1a1a1a]/60 mt-1">Öğrenci ön başvurularını yönet ve dışa aktar.</p>
          </div>
          <div className="flex gap-3">
            <button onClick={load} className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2.5 text-sm font-medium hover:bg-[#f7f9fa] transition-colors">
              <RefreshCw size={16} /> Yenile
            </button>
            <button onClick={exportCsv} data-testid="export-csv-btn" className="inline-flex items-center gap-2 rounded-full bg-[#FFE500] px-5 py-2.5 text-sm font-semibold text-[#1a1a1a] hover:bg-[#EACE00] transition-colors">
              <Download size={16} /> Excel / CSV
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <StatCard icon={Users} label="Toplam Başvuru" value={stats?.total ?? "-"} />
          <StatCard icon={Sparkles} label="Yeni Başvuru" value={stats?.new ?? "-"} accent />
          <StatCard icon={GraduationCap} label="En Çok İstenen" value={stats?.by_department?.[0]?.department ?? "-"} />
        </div>

        <div className="rounded-3xl bg-white border border-black/5 shadow-[0_8px_32px_rgba(0,0,0,0.04)] overflow-hidden">
          <div className="overflow-x-auto">
            <Table data-testid="applications-table">
              <TableHeader>
                <TableRow className="bg-[#f7f9fa]">
                  <TableHead>Ad Soyad</TableHead>
                  <TableHead>İletişim</TableHead>
                  <TableHead>Lise / Ort.</TableHead>
                  <TableHead>Bölüm</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Tarih</TableHead>
                  <TableHead className="text-right">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-[#1a1a1a]/50">Yükleniyor...</TableCell></TableRow>
                ) : apps.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-[#1a1a1a]/50">Henüz başvuru yok.</TableCell></TableRow>
                ) : (
                  apps.map((a) => (
                    <TableRow key={a.id} data-testid={`app-row-${a.id}`}>
                      <TableCell className="font-semibold text-[#1a1a1a]">{a.full_name}</TableCell>
                      <TableCell className="text-sm">
                        <div>{a.phone}</div>
                        <div className="text-[#1a1a1a]/50">{a.email || "-"}</div>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div>{a.high_school || "-"}</div>
                        <div className="text-[#1a1a1a]/50">{a.gpa || "-"}</div>
                      </TableCell>
                      <TableCell><span className="font-medium text-[#1B19A8]">{a.department}</span></TableCell>
                      <TableCell>
                        <Select value={a.status} onValueChange={(v) => changeStatus(a.id, v)}>
                          <SelectTrigger className={`h-8 w-[160px] border-0 text-xs font-semibold ${STATUS[a.status]?.cls || ""}`} data-testid={`status-select-${a.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS).map(([k, v]) => (
                              <SelectItem key={k} value={k}>{v.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-sm text-[#1a1a1a]/60 whitespace-nowrap">{fmtDate(a.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <button onClick={() => remove(a.id)} data-testid={`delete-btn-${a.id}`} className="grid place-items-center w-8 h-8 rounded-lg text-red-500 hover:bg-red-50 transition-colors ml-auto">
                          <Trash2 size={16} />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-3xl border p-6 ${accent ? "bg-[#1B19A8] border-[#1B19A8] text-white" : "bg-white border-black/5 text-[#1a1a1a]"}`}>
      <span className={`grid place-items-center w-10 h-10 rounded-xl mb-4 ${accent ? "bg-white/15" : "bg-[#1B19A8]/8 text-[#1B19A8]"}`}>
        <Icon size={20} />
      </span>
      <div className={`text-sm ${accent ? "text-white/70" : "text-[#1a1a1a]/55"}`}>{label}</div>
      <div className="font-display text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
